import { Button, styled, Stack, Link } from '@mui/material';
import { isEmpty, sortBy } from 'lodash-es';
import React, { FC, useState, useEffect } from 'react';
import { IJob, IJobCandidateInteractionResponse } from 'wisestep-api';

import WsColors from '../../WsColors';
import ActivityCard from '../ActivityCard/ActivityCard';
import Item from '../Item/Item';
import TextOnLine from '../TextOnLine/TextOnLine';
import WsTypography from '../WsTypography/WsTypography';

interface ColorProps {
  selectedColor?: string;
}

const Btn = styled(Button)(({ selectedColor }: ColorProps) => ({
  backgroundColor: selectedColor,
  fontSize: '14px',
  fontWeight: '600',
  borderRadius: '4px',
  color: WsColors.TheBlues500,
  borderColor: WsColors.TheBlues500,
  height: '50px',
  textTransform: 'none',
  '&:active': {
    backgroundColor: WsColors.TheBlues50,
  },
  '&:hover': {
    backgroundColor: WsColors.TheBlues50,
    borderColor: WsColors.TheBlues500,
  },
}));

interface IActivityTab {
  totalConversation: string | null;
  history: IJobCandidateInteractionResponse | null;
  conversations: IJobCandidateInteractionResponse[];
  onSubmissionStatusUpdate?: (interactionId: string | null) => void;
  onActivityUpdate?: (interactionId: string | null) => void;
  onLoadMore: () => void;
  onClickOnJobCode?: (jobId: string) => void;
  loadHistory: (id: string) => void;
  taskLisiting?: JSX.Element;
  isInDnc?: boolean;
  showJobRealtedConverationOnTop?: string | null;
  selectedJob?: IJob | null;
  getFieldSettings: (isMapping: boolean) => any;
  userEmail?: string;
  jobAdminTeam?: boolean;
}

const ActivityTab: FC<IActivityTab> = ({
  conversations,
  history,
  onActivityUpdate,
  onSubmissionStatusUpdate,
  totalConversation,
  onLoadMore,
  onClickOnJobCode,
  loadHistory,
  taskLisiting,
  isInDnc = false,
  showJobRealtedConverationOnTop,
  selectedJob,
  getFieldSettings,
  userEmail,
  jobAdminTeam,
}) => {
  const [interactions, setInteractions] = useState<IJobCandidateInteractionResponse[]>([]);
  const [submissions, setSubmissions] = useState<IJobCandidateInteractionResponse[]>([]);
  const [selected, setSelected] = useState<string>('conversation');
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    if (showJobRealtedConverationOnTop && selectedJob) {
      const jobConversation = conversations.filter((data) => data.jobCode === selectedJob.jobCode);
      const otherConversations = conversations.filter(
        (data) => data.jobCode !== selectedJob.jobCode
      );
      const interaction = sortBy(otherConversations, (candidate) => {
        if (candidate.modifiedOn) {
          return new Date(candidate.modifiedOn);
        }
        return candidate.modifiedOn;
      });
      if (!isEmpty(jobConversation)) {
        interaction.push(jobConversation[0]);
      }
      setInteractions(interaction.reverse());
    } else {
      setInteractions(
        sortBy(conversations, (candidate) => {
          if (candidate.modifiedOn) {
            return new Date(candidate.modifiedOn);
          }
          return candidate.modifiedOn;
        }).reverse()
      );
    }
    setSubmissions(
      conversations.filter((candidate: IJobCandidateInteractionResponse) =>
        candidate.candidateStatusTags.includes('submitted')
      )
    );
  }, [conversations, selectedJob, showJobRealtedConverationOnTop]);

  const noDataScreen = (text: string) => (
    <Stack alignItems="center" mt={20}>
      <WsTypography bold>{text}</WsTypography>
    </Stack>
  );

  return (
    <>
      <Item>
        <Stack direction="row" spacing={2}>
          <Btn
            variant="outlined"
            onClick={() => setSelected('conversation')}
            selectedColor={selected === 'conversation' ? WsColors.TheBlues100 : WsColors.White}
          >
            {interactions.length}
            <br />
            Conversations
          </Btn>
          <Btn
            variant="outlined"
            onClick={() => setSelected('submission')}
            selectedColor={selected === 'submission' ? WsColors.TheBlues100 : WsColors.White}
          >
            {submissions.length}
            <br />
            Submissions
          </Btn>
        </Stack>
      </Item>
      {selected === 'conversation' && (
        <>
          {totalConversation &&
            Number(totalConversation) > 5 &&
            interactions.length > 0 &&
            !isLoaded && (
              <Item>
                <TextOnLine>
                  <Link
                    underline="none"
                    onClick={() => {
                      setIsLoaded(true);
                      onLoadMore();
                    }}
                    sx={{ cursor: 'pointer' }}
                  >
                    Showing latest {interactions.length} conversations. Click here to load more.
                  </Link>
                </TextOnLine>
              </Item>
            )}
          {interactions.map((data, index: number) => (
            <Item key={index} sx={{ pb: '0px' }}>
              <ActivityCard
                history={history}
                conversation={data}
                getFieldSettings={getFieldSettings}
                onActivityUpdate={() => onActivityUpdate && onActivityUpdate(data.id)}
                onClickOnJobCode={(jobId) => onClickOnJobCode && onClickOnJobCode(jobId)}
                loadHistory={loadHistory}
                onUpdateSubmissionStatus={() =>
                  onSubmissionStatusUpdate && onSubmissionStatusUpdate(data.id)
                }
                taskLisiting={taskLisiting}
                isInDnc={isInDnc}
                userEmail={userEmail}
                jobAdminTeam={jobAdminTeam}
              />
            </Item>
          ))}
          {interactions.length === 0 && noDataScreen('No Conversation Found')}
          {totalConversation &&
            Number(totalConversation) > 5 &&
            interactions.length > 0 &&
            !isLoaded && (
              <Item>
                <TextOnLine>
                  <Link
                    underline="none"
                    onClick={() => {
                      setIsLoaded(true);
                      onLoadMore();
                    }}
                    sx={{ cursor: 'pointer' }}
                  >
                    Load More
                  </Link>
                </TextOnLine>
              </Item>
            )}
        </>
      )}
      {selected === 'submission' && (
        <>
          {totalConversation &&
            Number(totalConversation) > 5 &&
            submissions.length > 0 &&
            !isLoaded && (
              <Item>
                <TextOnLine>
                  <Link
                    underline="none"
                    onClick={() => {
                      setIsLoaded(true);
                      onLoadMore();
                    }}
                    sx={{ cursor: 'pointer' }}
                  >
                    Showing latest {submissions.length} conversations. Click here to load more.
                  </Link>
                </TextOnLine>
              </Item>
            )}
          {submissions.map((data, index: number) => (
            <Item key={index} sx={{ pb: '0px' }}>
              <ActivityCard
                history={history}
                conversation={data}
                getFieldSettings={getFieldSettings}
                onActivityUpdate={() => onActivityUpdate && onActivityUpdate(data.id)}
                loadHistory={loadHistory}
                onClickOnJobCode={(jobId) => onClickOnJobCode && onClickOnJobCode(jobId)}
                onUpdateSubmissionStatus={() =>
                  onSubmissionStatusUpdate && onSubmissionStatusUpdate(data.id)
                }
                taskLisiting={taskLisiting}
                isInDnc={isInDnc}
                userEmail={userEmail}
                jobAdminTeam={jobAdminTeam}
              />
            </Item>
          ))}
          {submissions.length === 0 && noDataScreen('No Submission Found')}
          {totalConversation &&
            Number(totalConversation) > 5 &&
            submissions.length > 0 &&
            !isLoaded && (
              <Item>
                <TextOnLine>
                  <Link
                    underline="none"
                    onClick={() => {
                      setIsLoaded(true);
                      onLoadMore();
                    }}
                    sx={{ cursor: 'pointer' }}
                  >
                    Load More
                  </Link>
                </TextOnLine>
              </Item>
            )}
        </>
      )}
    </>
  );
};

export default ActivityTab;
